// Production entitlement tree templates, converted from the bw-set-entitle
// seed resources (storage format) into the getDocumentList response shape the
// invite flow, team member detail and role comparison read. Importing the seed
// files keeps the demo in sync with the role definitions of each release.
import financialSpecialistSeed from "../../../../bw-set-entitle/src/resources/FinancialSpecialist.json";
import memberSeed from "../../../../bw-set-entitle/src/resources/Member.json";
import workspaceOwnerSeed from "../../../../bw-set-entitle/src/resources/WorkspaceOwner.json";

type SeedFeature = {
  feature: string;
  isVisibleForClient?: boolean;
  isAccessible?: boolean;
};
type SeedFeatureGroup = {
  featureGroupBasicInfo: { featureGroup: string; isVisibleForClient?: boolean; accessLevel?: string };
  features: SeedFeature[];
};
type SeedService = {
  serviceBasicInfo: { service: string; isVisibleForClient?: boolean; accessLevel?: string };
  featureGroups: SeedFeatureGroup[];
};
type SeedServiceGroup = {
  serviceGroupBasicInfo: { serviceGroup: string; isVisibleForClient?: boolean; accessLevel?: string };
  services: SeedService[];
};
type SeedTemplate = {
  id: string;
  documentData: {
    entitlementTreeTemplateBasicInfo: { roleRelationType: string; roleType?: string };
    serviceGroups: SeedServiceGroup[];
  };
};

const SEEDS: SeedTemplate[] = [
  workspaceOwnerSeed as SeedTemplate,
  financialSpecialistSeed as SeedTemplate,
  memberSeed as SeedTemplate,
];

const header = (entityType: string, entityId: string) => ({
  entityType,
  definitionVersion: "1",
  entityId,
  entityContext: "LiveEntitlementTreeTemplate",
});

const list = <T,>(items: T[]) => ({
  pagingResponse: { totalItemsCount: items.length },
  returnedItems: items,
});

function templateDoc(seed: SeedTemplate) {
  const { roleRelationType, roleType } = seed.documentData.entitlementTreeTemplateBasicInfo;
  const id = (...parts: string[]) => ["demo", roleRelationType, ...parts].join("-");
  return {
    documentHeader: {
      id: `demo-entitlement-template-${roleRelationType}`,
      documentType: "LiveEntitlementTreeTemplate",
      _ts: Math.floor(Date.now() / 1000),
      _etag: '"demo-etag"',
    },
    entitlementTreeTemplateBasicInfo: {
      entityData: { roleRelationType, ...(roleType ? { roleType } : {}) },
    },
    serviceGroups: list(
      seed.documentData.serviceGroups.map((sg) => ({
        entityHeader: header("ServiceGroup", id("sg", sg.serviceGroupBasicInfo.serviceGroup)),
        entityData: {
          serviceGroupBasicInfo: { entityData: sg.serviceGroupBasicInfo },
          services: list(
            sg.services.map((svc) => ({
              entityHeader: header("Service", id("svc", svc.serviceBasicInfo.service)),
              entityData: {
                serviceBasicInfo: { entityData: svc.serviceBasicInfo },
                featureGroups: list(
                  svc.featureGroups.map((fg) => ({
                    entityHeader: header(
                      "FeatureGroup",
                      id("fg", svc.serviceBasicInfo.service, fg.featureGroupBasicInfo.featureGroup),
                    ),
                    entityData: {
                      featureGroupBasicInfo: { entityData: fg.featureGroupBasicInfo },
                      features: list(
                        fg.features.map((f) => ({
                          entityHeader: header("Feature", id("f", svc.serviceBasicInfo.service, f.feature)),
                          entityData: {
                            feature: f.feature,
                            isAccessible: f.isAccessible ?? false,
                            isVisibleForClient: f.isVisibleForClient ?? false,
                          },
                        })),
                      ),
                    },
                  })),
                ),
              },
            })),
          ),
        },
      })),
    ),
  };
}

/** Templates for the role types enabled in this build (VITE_ENABLED_CLIENT_USER_ROLE_TYPES). */
export function buildEntitlementTemplateList() {
  const enabled = String(import.meta.env.VITE_ENABLED_CLIENT_USER_ROLE_TYPES ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const docs = SEEDS.filter(
    (s) =>
      enabled.length === 0 ||
      enabled.includes(s.documentData.entitlementTreeTemplateBasicInfo.roleRelationType),
  ).map(templateDoc);
  return {
    documentList: docs,
    pagingResponse: { totalItemsCount: docs.length, hasNextPage: false, pageNumber: 1 },
  };
}
